/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AccessPolicyRead } from '../models/AccessPolicyRead';
import type { AccessPolicyWithRolePayload } from '../models/AccessPolicyWithRolePayload';
import type { AccessRoleCreate } from '../models/AccessRoleCreate';
import type { AccessRoleCreateWithPolicies } from '../models/AccessRoleCreateWithPolicies';
import type { AccessRoleRead } from '../models/AccessRoleRead';
import type { AccessRoleSummary } from '../models/AccessRoleSummary';
import type { AddStaffMembershipPayload } from '../models/AddStaffMembershipPayload';
import type { AIConceptEnum } from '../models/AIConceptEnum';
import type { AILuWebSocketPayload } from '../models/AILuWebSocketPayload';
import type { AuthenticationMethodEnum } from '../models/AuthenticationMethodEnum';
import type { AuthzScopeEnum } from '../models/AuthzScopeEnum';
import type { BelongsToConfig } from '../models/BelongsToConfig';
import type { ClusterDefaultRoleUpdate } from '../models/ClusterDefaultRoleUpdate';
import type { ClusterRead } from '../models/ClusterRead';
import type { ConfigTypeEnum } from '../models/ConfigTypeEnum';
import type { ConfigValueEnum } from '../models/ConfigValueEnum';
import type { CreateClusterPayload } from '../models/CreateClusterPayload';
import type { CurrencyEnum } from '../models/CurrencyEnum';
import type { DayEnum } from '../models/DayEnum';
import type { DayOfWeekEnum } from '../models/DayOfWeekEnum';
import type { DeliverableCollectionStatusEnum } from '../models/DeliverableCollectionStatusEnum';
import type { DeliverableCollectionStrategyEnum } from '../models/DeliverableCollectionStrategyEnum';
import type { EntityRead } from '../models/EntityRead';
import type { FeatureEnum } from '../models/FeatureEnum';
import type { FrequencyTypeEnum } from '../models/FrequencyTypeEnum';
import type { InterpolationVariableEnum } from '../models/InterpolationVariableEnum';
import type { LuFeatureEnum } from '../models/LuFeatureEnum';
import type { LuFeedbackRatingEnum } from '../models/LuFeedbackRatingEnum';
import type { LuSource } from '../models/LuSource';
import type { Me } from '../models/Me';
import type { MembershipAssignmentRead } from '../models/MembershipAssignmentRead';
import type { MembershipUpdatePayload } from '../models/MembershipUpdatePayload';
import type { MembershipWithEntities } from '../models/MembershipWithEntities';
import type { MultiFactorMethodEnum } from '../models/MultiFactorMethodEnum';
import type { MyLoginConfig } from '../models/MyLoginConfig';
import type { NamespaceConfigDictOld } from '../models/NamespaceConfigDictOld';
import type { NumericReturnTypeEnum } from '../models/NumericReturnTypeEnum';
import type { PolicyRoleAssignmentUpdate } from '../models/PolicyRoleAssignmentUpdate';
import type { PropertyLxReferenceGroup } from '../models/PropertyLxReferenceGroup';
import type { QuarterMonthEnum } from '../models/QuarterMonthEnum';
import type { RelDateModifierEnum } from '../models/RelDateModifierEnum';
import type { RelDatePeriodEnum } from '../models/RelDatePeriodEnum';
import type { RoleCreateWithPolicies } from '../models/RoleCreateWithPolicies';
import type { RoleMembershipAssignmentUpdate } from '../models/RoleMembershipAssignmentUpdate';
import type { RolePolicyAssignmentUpdate } from '../models/RolePolicyAssignmentUpdate';
import type { RouteNames } from '../models/RouteNames';
import type { SourceTypeEnum } from '../models/SourceTypeEnum';
import type { StatementTypeEnum } from '../models/StatementTypeEnum';
import type { TaskResultDomain } from '../models/TaskResultDomain';
import type { TaskStateEnum } from '../models/TaskStateEnum';
import type { TodoTaskStatusEnum } from '../models/TodoTaskStatusEnum';
import type { Token } from '../models/Token';
import type { UpdateProfilePayload } from '../models/UpdateProfilePayload';
import type { UserRead } from '../models/UserRead';
import type { VariantTypeEnum } from '../models/VariantTypeEnum';
import type { WebSocketMessageDomain } from '../models/WebSocketMessageDomain';
import type { WorkflowActionTypeEnum } from '../models/WorkflowActionTypeEnum';
import type { WorkflowLabelEnum } from '../models/WorkflowLabelEnum';
import type { WorkflowMessageVariableEnum } from '../models/WorkflowMessageVariableEnum';
import type { XthOccurrenceEnum } from '../models/XthOccurrenceEnum';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DefaultService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get Me
     * @returns Me Successful Response
     * @throws ApiError
     */
    public getMe(): CancelablePromise<Me> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/me',
        });
    }

    /**
     * Update My Profile
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateMyProfile(
        requestBody: UpdateProfilePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/me/update-profile',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add Staff User
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public addStaffUser(
        requestBody: AddStaffMembershipPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/add-staff-user',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Initialize Cluster With Required Defaults
     * Initialize a new cluster with all required defaults
     * @param requestBody
     * @returns ClusterRead Successful Response
     * @throws ApiError
     */
    public initializeClusterWithRequiredDefaults(
        requestBody: CreateClusterPayload,
    ): CancelablePromise<ClusterRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/create-cluster',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get My Login Config
     * Fetches required login details for a user email.
     * If the email is invalid, it will return default login settings to
     * disguise implementation details of our system.
     * @param email
     * @returns MyLoginConfig Successful Response
     * @throws ApiError
     */
    public getMyLoginConfig(
        email: string,
    ): CancelablePromise<MyLoginConfig> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/my-login-config',
            query: {
                'email': email,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Impersonate User
     * @param targetUserId
     * @returns Token Successful Response
     * @throws ApiError
     */
    public impersonateUser(
        targetUserId: string,
    ): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/impersonate',
            query: {
                'target_user_id': targetUserId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Impersonate User
     * @returns Token Successful Response
     * @throws ApiError
     */
    public cancelImpersonateUser(): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/cancel-impersonate',
        });
    }

    /**
     * Search Users To Impersonate
     * @param search
     * @returns UserRead Successful Response
     * @throws ApiError
     */
    public searchUsersToImpersonate(
        search: string,
    ): CancelablePromise<Array<UserRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/search-users-to-impersonate',
            query: {
                'search': search,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cluster Access Roles
     * @param clusterId
     * @param entityId
     * @returns AccessRoleSummary Successful Response
     * @throws ApiError
     */
    public listClusterAccessRoles(
        clusterId: string,
        entityId?: (string | null),
    ): CancelablePromise<Array<AccessRoleSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-cluster-access-roles',
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Entity Access Roles
     * List access roles for an entity (requires entity admin permission)
     * @param entityId
     * @returns AccessRoleSummary Successful Response
     * @throws ApiError
     */
    public listEntityAccessRoles(
        entityId: string,
    ): CancelablePromise<Array<AccessRoleSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-entity-access-roles',
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cluster Access Role
     * @param clusterId
     * @param requestBody
     * @returns AccessRoleRead Successful Response
     * @throws ApiError
     */
    public createClusterAccessRole(
        clusterId: string,
        requestBody: AccessRoleCreateWithPolicies,
    ): CancelablePromise<AccessRoleRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/create-cluster-access-role',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cluster Access Role With Policies
     * Create a new access role with initial policy assignments.
     *
     * This endpoint allows creating a role and assigning it to policies in a single operation.
     * @param clusterId
     * @param requestBody
     * @returns AccessRoleRead Successful Response
     * @throws ApiError
     */
    public createClusterAccessRoleWithPolicies(
        clusterId: string,
        requestBody: RoleCreateWithPolicies,
    ): CancelablePromise<AccessRoleRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/roles/with-policies',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Cluster Access Role
     * Get an access role by ID.
     * @param clusterId
     * @param roleId
     * @returns AccessRoleRead Successful Response
     * @throws ApiError
     */
    public getClusterAccessRole(
        clusterId: string,
        roleId: string,
    ): CancelablePromise<AccessRoleRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/get-cluster-access-role',
            query: {
                'cluster_id': clusterId,
                'role_id': roleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cluster Access Role
     * Update an access role.
     * @param clusterId
     * @param roleId
     * @param requestBody
     * @returns AccessRoleRead Successful Response
     * @throws ApiError
     */
    public updateClusterAccessRole(
        clusterId: string,
        roleId: string,
        requestBody: AccessRoleCreate,
    ): CancelablePromise<AccessRoleRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/update-cluster-access-role',
            query: {
                'cluster_id': clusterId,
                'role_id': roleId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Cluster Access Role
     * @param clusterId
     * @param roleId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteClusterAccessRole(
        clusterId: string,
        roleId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/delete-cluster-access-role',
            query: {
                'cluster_id': clusterId,
                'role_id': roleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Membership Assignments
     * List all membership assignments, optionally filtered by membership or role.
     * @param clusterId
     * @param roleId
     * @param membershipId
     * @returns MembershipAssignmentRead Successful Response
     * @throws ApiError
     */
    public listMembershipAssignments(
        clusterId: string,
        roleId: string,
        membershipId?: string,
    ): CancelablePromise<Array<MembershipAssignmentRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/assignments',
            query: {
                'cluster_id': clusterId,
                'role_id': roleId,
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Policies For Cluster Role
     * List all policies assigned to a specific role.
     *
     * Args:
     * role_id: The ID of the role to get policies for
     * cluster_id: The ID of the cluster the role belongs to
     *
     * Returns:
     * List of access policies assigned to the role
     * @param clusterId
     * @param roleId
     * @returns AccessPolicyRead Successful Response
     * @throws ApiError
     */
    public listPoliciesForClusterRole(
        clusterId: string,
        roleId: string,
    ): CancelablePromise<Array<AccessPolicyRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-policies-for-cluster-role',
            query: {
                'cluster_id': clusterId,
                'role_id': roleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Access Policies For Cluster
     * @param clusterId
     * @returns AccessPolicyRead Successful Response
     * @throws ApiError
     */
    public listAccessPoliciesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<AccessPolicyRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-access-policies-for-cluster',
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Staff Memberships For Cluster
     * List membership IDs for users who are members of a cluster via their staff role.
     *
     * This endpoint is used by the UI to filter out staff users from certain operations,
     * such as role assignment dialogs, where staff users should be handled differently.
     *
     * Args:
     * cluster_id: Optional ID of the cluster to filter by
     * authorization_service: The authorization service instance
     * user: The authenticated user making the request
     *
     * Returns:
     * A list of membership IDs belonging to staff users for the specified cluster
     * @param clusterId
     * @returns string Successful Response
     * @throws ApiError
     */
    public listStaffMembershipsForCluster(
        clusterId: string,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-staff-memberships-for-cluster',
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cluster Access Policy
     * @param clusterId
     * @param requestBody
     * @returns AccessPolicyRead Successful Response
     * @throws ApiError
     */
    public createClusterAccessPolicy(
        clusterId: string,
        requestBody: AccessPolicyWithRolePayload,
    ): CancelablePromise<AccessPolicyRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/create-cluster-access-policy',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cluster Policy Role Assignments
     * Update role assignments for a policy.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateClusterPolicyRoleAssignments(
        clusterId: string,
        requestBody: PolicyRoleAssignmentUpdate,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/update-cluster-policy-role-assignments',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cluster Role Policy Assignments
     * Update policy assignments for a role.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateClusterRolePolicyAssignments(
        clusterId: string,
        requestBody: RolePolicyAssignmentUpdate,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/update-cluster-role-policy-assignments',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Cluster Access Policy
     * @param clusterId
     * @param policyId
     * @returns AccessPolicyRead Successful Response
     * @throws ApiError
     */
    public getClusterAccessPolicy(
        clusterId: string,
        policyId: string,
    ): CancelablePromise<AccessPolicyRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/get-cluster-access-policy',
            query: {
                'cluster_id': clusterId,
                'policy_id': policyId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cluster Access Policy
     * @param clusterId
     * @param policyId
     * @param requestBody
     * @returns AccessPolicyRead Successful Response
     * @throws ApiError
     */
    public updateClusterAccessPolicy(
        clusterId: string,
        policyId: string,
        requestBody: AccessPolicyWithRolePayload,
    ): CancelablePromise<AccessPolicyRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/update-cluster-access-policy',
            query: {
                'cluster_id': clusterId,
                'policy_id': policyId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Cluster Access Policy
     * @param clusterId
     * @param policyId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteClusterAccessPolicy(
        clusterId: string,
        policyId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/delete-cluster-access-policy',
            query: {
                'cluster_id': clusterId,
                'policy_id': policyId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Roles For Cluster Policy
     * List all roles that have a specific policy assigned.
     *
     * Args:
     * policy_id: The ID of the policy to get roles for
     * cluster_id: The ID of the cluster the policy belongs to
     *
     * Returns:
     * List of access roles that have the specified policy assigned
     * @param clusterId
     * @param policyId
     * @returns AccessRoleRead Successful Response
     * @throws ApiError
     */
    public listRolesForClusterPolicy(
        clusterId: string,
        policyId: string,
    ): CancelablePromise<Array<AccessRoleRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-roles-for-cluster-policy',
            query: {
                'cluster_id': clusterId,
                'policy_id': policyId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Role Membership Assignments
     * @param roleId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateRoleMembershipAssignments(
        roleId: string,
        requestBody: RoleMembershipAssignmentUpdate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/role-membership-assignments',
            query: {
                'role_id': roleId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Resources By Type
     * List all resources of a specific type for a cluster.
     *
     * This endpoint returns resources that can be used in policy resource selectors.
     * The response format includes id and name properties for each resource.
     * @param clusterId
     * @param resourceType
     * @returns any Successful Response
     * @throws ApiError
     */
    public listResourcesByType(
        clusterId: string,
        resourceType: string,
    ): CancelablePromise<Array<Record<string, any>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/resources',
            query: {
                'cluster_id': clusterId,
                'resource_type': resourceType,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Check Cluster Admin Access
     * Check if the current user has admin access to a specific cluster.
     *
     * Admin access is granted if the user is either a staff user or
     * has a role with admin permissions for the cluster.
     *
     * Args:
     * cluster_id: The ID of the cluster to check access for
     *
     * Returns:
     * A dictionary with a boolean 'has_admin_access' indicating if the user has admin access
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public checkClusterAdminAccess(
        clusterId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/cluster/{cluster_id}/has-admin-access',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cluster Default Role
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateClusterDefaultRole(
        clusterId: string,
        requestBody: ClusterDefaultRoleUpdate,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/update-cluster-default-role',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Namespace Entities
     * @param namespaceId
     * @returns EntityRead Successful Response
     * @throws ApiError
     */
    public listNamespaceEntities(
        namespaceId: string,
    ): CancelablePromise<Array<EntityRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-namespace-entities',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Clusters
     * @returns ClusterRead Successful Response
     * @throws ApiError
     */
    public listClusters(): CancelablePromise<Array<ClusterRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-clusters',
        });
    }

    /**
     * List Cluster Memberships For User
     * @returns MembershipWithEntities Successful Response
     * @throws ApiError
     */
    public listClusterMembershipsForUser(): CancelablePromise<Array<MembershipWithEntities>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/list-cluster-memberships-for-user',
        });
    }

    /**
     * Remove Membership
     * @param membershipId
     * @returns any Successful Response
     * @throws ApiError
     */
    public removeMembership(
        membershipId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/remove-membership/{membership_id}',
            path: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Membership
     * @param membershipId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateMembership(
        membershipId: string,
        requestBody: MembershipUpdatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/update-membership/{membership_id}',
            path: {
                'membership_id': membershipId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Constants
     * The purpose of this endpoint is to expose Enums that may not be exposed through other endpoints
     * to our openapi schema. This makes it possible for us to use these Enums on the frontend through
     * our client generation process.
     * @returns any Successful Response
     * @throws ApiError
     */
    public getConstants(): CancelablePromise<Array<(CurrencyEnum | ConfigTypeEnum | ConfigValueEnum | StatementTypeEnum | AuthzScopeEnum | VariantTypeEnum | WorkflowLabelEnum | WorkflowActionTypeEnum | TaskStateEnum | InterpolationVariableEnum | SourceTypeEnum | RouteNames | MultiFactorMethodEnum | AuthenticationMethodEnum | DeliverableCollectionStrategyEnum | DeliverableCollectionStatusEnum | RelDatePeriodEnum | RelDateModifierEnum | WebSocketMessageDomain | AILuWebSocketPayload | FrequencyTypeEnum | DayEnum | DayOfWeekEnum | QuarterMonthEnum | XthOccurrenceEnum | FeatureEnum | NamespaceConfigDictOld | BelongsToConfig | NumericReturnTypeEnum | WorkflowMessageVariableEnum | PropertyLxReferenceGroup | LuSource | TodoTaskStatusEnum | LuFeedbackRatingEnum | LuFeatureEnum | AIConceptEnum)>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/constants',
        });
    }

    /**
     * Get Task Status
     * @param signedMessageKey
     * @returns TaskResultDomain Successful Response
     * @throws ApiError
     */
    public getTaskStatus(
        signedMessageKey: string,
    ): CancelablePromise<TaskResultDomain> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/task/{signed_message_key}',
            path: {
                'signed_message_key': signedMessageKey,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
